import { createAction, props } from '@ngrx/store';
import {
  TransactionFilters,
  TransactionItem,
  TransactionType,
  TransactionProjectionStatusEnum,
} from '../models/transaction.model';
import type {
  NetworkFeeData,
  WithdrawalLimitSummaryData,
} from '../models/transaction.model';
import type {
  GetTransactionDetailResponse,
  GetSigningNotificationChannelsResponse,
  GetTransactionNetworkDetailsResponse,
  GetTransactionBlockchainDetailsResponse,
  TransactionDetailFailure,
  CreateWithdrawalRequestBody,
  CreateWithdrawalResponse,
  TransactionStep01DetailsData,
  FeeOptionsData,
  FeeChoice,
} from '../models/transaction.model';
import type { ApprovalProgressData } from '@/app/shared/models/approval.model';
import type { TransactionExecutionStep } from '../models/transaction.model';

/** List */
export const loadTransactions = createAction(
  '[Transaction] Load Transactions',
  props<{ listType?: TransactionType }>()
);

export const loadTransactionsSuccess = createAction(
  '[Transaction] Load Transactions Success',
  props<{
    data: TransactionItem[];
    total: number;
  }>()
);

export const loadTransactionsFailure = createAction(
  '[Transaction] Load Transactions Failure',
  props<{ error: string }>()
);

export const setActiveTab = createAction(
  '[Transaction] Set Active Tab',
  props<{ tab: TransactionType }>()
);

export const setPagination = createAction(
  '[Transaction] Set Pagination',
  props<{ pageIndex: number; pageSize: number }>()
);

export const setSort = createAction(
  '[Transaction] Set Sort',
  props<{ active: string; direction: 'ASC' | 'DESC' }>()
);

export const setFilters = createAction(
  '[Transaction] Set Filters',
  props<{ filters: TransactionFilters }>()
);

export const clearFilters = createAction('[Transaction] Clear Filters');

export const removeFilterChip = createAction(
  '[Transaction] Remove Filter Chip',
  props<{ key: string; value?: string }>()
);

export const setSearch = createAction(
  '[Transaction] Set Search',
  props<{ searchTerm: string }>()
);

export const openFilterModal = createAction('[Transaction] Open Filter Modal');

export const closeFilterModal = createAction(
  '[Transaction] Close Filter Modal'
);

/** Detail */
export const loadTransactionDetail = createAction(
  '[Transaction] Load Transaction Detail',
  props<{ transactionId: string }>()
);

export const loadTransactionDetailSuccess = createAction(
  '[Transaction] Load Transaction Detail Success',
  props<{ response: GetTransactionDetailResponse }>()
);

export const loadTransactionDetailFailure = createAction(
  '[Transaction] Load Transaction Detail Failure',
  props<{ error: string }>()
);

/* -------------------------------------------------------------------------- */
/* WebSocket-driven "apply from payload" actions (no HTTP refetch).            */
/*                                                                            */
/* These replace the old realtime-triggered `refresh*` round-trips: instead   */
/* of re-fetching, the realtime dispatcher patches the open detail's slices   */
/* directly from the websocket payload. Every action is gated in the reducer  */
/* on the open detail's wallet id (`detail.data.wallet.id === walletId`) so an */
/* event for another wallet's transaction is a no-op. The one initial fetch   */
/* per section (on page open) still establishes the baseline these patch.     */
/* -------------------------------------------------------------------------- */

/**
 * Patch step-4 network details (txid + explorer URL + status) directly from a
 * `TRANSACTION_BROADCASTED` payload. No-op when network-details hasn't been
 * loaded yet (the initial fetch carries the same data).
 */
export const applyNetworkDetailsFromEvent = createAction(
  '[Transaction] Apply Network Details From Event',
  props<{
    walletId: string;
    transactionHash: string;
    explorerUrl: string | null;
    projectionState: TransactionProjectionStatusEnum;
    updatedAt: string;
  }>()
);

/**
 * Patch step-4 confirmation progress (`confirmCount` / `blockNo`) directly from
 * a `BLOCKCHAIN_CONFIRMATIONS_UPDATED` payload. The static `confirmationMode` /
 * `acceptableConfirmCount` denominator comes from the initial fetch and is kept.
 */
export const applyBlockchainDetailsFromEvent = createAction(
  '[Transaction] Apply Blockchain Details From Event',
  props<{
    walletId: string;
    confirmCount: number;
    blockNo: number | null;
    updatedAt: string;
  }>()
);

/**
 * Patch the parent detail's projection `status.state` (and, when terminal, the
 * `failure`) directly from a websocket payload. Also keeps the network /
 * blockchain slice `status` fields in lockstep, mirroring what a full refetch
 * would do.
 */
export const applyDetailStatusFromEvent = createAction(
  '[Transaction] Apply Detail Status From Event',
  props<{
    walletId: string;
    projectionState: TransactionProjectionStatusEnum;
    failure?: TransactionDetailFailure | null;
  }>()
);

/** Patch the step-3 signing status directly from a `SIGNING_PROGRESS_UPDATED` payload. */
export const applySigningStatusFromEvent = createAction(
  '[Transaction] Apply Signing Status From Event',
  props<{
    walletId: string;
    projectionState: TransactionProjectionStatusEnum;
    updatedAt: string;
  }>()
);

export const clearTransactionDetail = createAction(
  '[Transaction] Clear Transaction Detail'
);

/** Transaction detail — selected step in UI */
export const setDetailSelectedStep = createAction(
  '[Transaction] Set Detail Selected Step',
  props<{ step: TransactionExecutionStep }>()
);

/** Step 02 — approval requirements progress (policy-management) */
export const loadApprovalProgressByExternalId = createAction(
  '[Transaction] Load Approval Progress By External Id',
  props<{ externalRequestId: string }>()
);

export const loadApprovalProgressByExternalIdSuccess = createAction(
  '[Transaction] Load Approval Progress By External Id Success',
  props<{ data: ApprovalProgressData }>()
);

export const loadApprovalProgressByExternalIdFailure = createAction(
  '[Transaction] Load Approval Progress By External Id Failure',
  props<{ error: string }>()
);

/**
 * Silent re-fetch of GET /approval-requests/external/:id/progress. Used by
 * realtime-driven refreshes (e.g. on `APPROVAL_APPROVED` /
 * `APPROVAL_STAGE_COMPLETED` envelopes whose `requestId` matches the open
 * detail's `approvalRequestId`). Pair with
 * {@link refreshApprovalProgressByExternalIdSuccess}; failures are
 * swallowed so the page keeps showing the previous progress snapshot.
 */
export const refreshApprovalProgressByExternalId = createAction(
  '[Transaction] Refresh Approval Progress By External Id',
  props<{ externalRequestId: string }>()
);

/** Approval-progress refresh from polling — does not toggle approval-progress loading UI. */
export const refreshApprovalProgressByExternalIdSuccess = createAction(
  '[Transaction] Refresh Approval Progress By External Id Success',
  props<{ data: ApprovalProgressData }>()
);

/** Step 01 — GET /transaction/:id/details */
export const loadTransactionStep01Details = createAction(
  '[Transaction] Load Transaction Step01 Details',
  props<{ transactionId: string }>()
);

export const loadTransactionStep01DetailsSuccess = createAction(
  '[Transaction] Load Transaction Step01 Details Success',
  props<{ data: TransactionStep01DetailsData }>()
);

export const loadTransactionStep01DetailsFailure = createAction(
  '[Transaction] Load Transaction Step01 Details Failure',
  props<{ error: string }>()
);

/** Signing step (notification channels) */
export const loadSigningChannels = createAction(
  '[Transaction] Load Signing Channels',
  props<{ transactionId?: string }>()
);

export const loadSigningChannelsSuccess = createAction(
  '[Transaction] Load Signing Channels Success',
  props<{ response: GetSigningNotificationChannelsResponse }>()
);

export const loadSigningChannelsFailure = createAction(
  '[Transaction] Load Signing Channels Failure',
  props<{ error: string }>()
);

/** Network step (step 4) */
export const loadNetworkDetails = createAction(
  '[Transaction] Load Network Details',
  props<{ transactionId: string }>()
);

export const loadNetworkDetailsSuccess = createAction(
  '[Transaction] Load Network Details Success',
  props<{ response: GetTransactionNetworkDetailsResponse }>()
);

export const loadNetworkDetailsFailure = createAction(
  '[Transaction] Load Network Details Failure',
  props<{ error: string }>()
);

/** Blockchain confirmations (step 4 — when projection is CONFIRMING) */
export const loadBlockchainDetails = createAction(
  '[Transaction] Load Blockchain Details',
  props<{ transactionId: string }>()
);

export const loadBlockchainDetailsSuccess = createAction(
  '[Transaction] Load Blockchain Details Success',
  props<{ response: GetTransactionBlockchainDetailsResponse }>()
);

export const loadBlockchainDetailsFailure = createAction(
  '[Transaction] Load Blockchain Details Failure',
  props<{ error: string }>()
);

/** Broadcast (step 4 — initial call, no body). May yield a verification challenge or directly succeed. */
export const broadcastTransaction = createAction(
  '[Transaction] Broadcast Transaction',
  props<{ transactionId: string }>()
);

/**
 * Backend returned 202 verification_required: the broadcast effect has
 * forwarded the challenge to the shared verification slice via
 * `setVerificationChallenge({ source: 'broadcast' })`. This action only
 * clears the local `broadcastSubmitting` flag — the actionId / method /
 * expiresAt live in the shared `verification` feature.
 */
export const broadcastTransactionVerificationRequired = createAction(
  '[Transaction] Broadcast Transaction Verification Required',
  props<{ transactionId: string }>()
);

export const broadcastTransactionSuccess = createAction(
  '[Transaction] Broadcast Transaction Success',
  props<{ response: GetTransactionDetailResponse }>()
);

/** `transactionId` gates the stamp to the detail the call was for (F-060). */
export const broadcastTransactionFailure = createAction(
  '[Transaction] Broadcast Transaction Failure',
  props<{ transactionId: string; failure: TransactionDetailFailure }>()
);

/**
 * Retry POST /broadcast with the verified `actionId` to actually broadcast on-chain.
 * Dispatched after the shared `submitVerificationCodeSuccess` action when
 * its `source === 'broadcast'`.
 */
export const confirmBroadcast = createAction(
  '[Transaction] Confirm Broadcast',
  props<{ transactionId: string; actionId: string }>()
);

/* -------------------------------------------------------------------------- */
/* User-selected fee at execution (Part D — step 3 "Choose fee & Execute")    */
/* -------------------------------------------------------------------------- */

/** Step 3: GET /transaction/:id/fee-options (tiered fee suggestions). */
export const loadFeeOptions = createAction(
  '[Transaction] Load Fee Options',
  props<{ transactionId: string }>()
);

export const loadFeeOptionsSuccess = createAction(
  '[Transaction] Load Fee Options Success',
  props<{ data: FeeOptionsData }>()
);

export const loadFeeOptionsFailure = createAction(
  '[Transaction] Load Fee Options Failure',
  props<{ error: string }>()
);

/** Step 3: Execute (initial call — `feeChoice` only). May yield a 202 challenge or directly succeed. */
export const executeTransaction = createAction(
  '[Transaction] Execute Transaction',
  props<{ transactionId: string; feeChoice: FeeChoice }>()
);

/**
 * Backend returned 202 verification_required: the execute effect forwarded the
 * challenge to the shared verification slice via
 * `setVerificationChallenge({ source: 'execute' })`. This action only clears the
 * local `executeSubmitting` flag — the chosen `feeChoice` is retained in
 * `pendingExecuteFeeChoice` for the verified retry.
 */
export const executeTransactionVerificationRequired = createAction(
  '[Transaction] Execute Transaction Verification Required',
  props<{ transactionId: string }>()
);

export const executeTransactionSuccess = createAction(
  '[Transaction] Execute Transaction Success',
  props<{ response: GetTransactionDetailResponse }>()
);

/** `transactionId` gates the stamp to the detail the call was for (F-060). */
export const executeTransactionFailure = createAction(
  '[Transaction] Execute Transaction Failure',
  props<{ transactionId: string; failure: TransactionDetailFailure }>()
);

/**
 * Retry POST /execute with the verified `actionId` (and the same `feeChoice`) to
 * kick off signing. Dispatched after the shared `submitVerificationCodeSuccess`
 * action when its `source === 'execute'`.
 */
export const confirmExecute = createAction(
  '[Transaction] Confirm Execute',
  props<{ transactionId: string; actionId: string; feeChoice: FeeChoice }>()
);

/** Verification-slice source for the Cancel step-up (shared with step 3's OTP modal). */
export const CANCEL_TRANSACTION_SOURCE = 'cancel-transaction';

/**
 * Step 3: Cancel an approved withdrawal still waiting for Execute (OWNER only).
 * Same step-up shape as Execute: a 202 challenge goes to the shared verification
 * slice with `source: 'cancel-transaction'`, and `reason` is kept for the retry.
 */
export const cancelTransaction = createAction(
  '[Transaction] Cancel Transaction',
  props<{ transactionId: string; reason: string | null }>()
);

export const cancelTransactionVerificationRequired = createAction(
  '[Transaction] Cancel Transaction Verification Required',
  props<{ transactionId: string }>()
);

export const confirmCancel = createAction(
  '[Transaction] Confirm Cancel',
  props<{ transactionId: string; actionId: string; reason: string | null }>()
);

export const cancelTransactionSuccess = createAction(
  '[Transaction] Cancel Transaction Success',
  props<{ response: GetTransactionDetailResponse }>()
);

/** The server's message is already toasted by the error interceptor. */
export const cancelTransactionFailure = createAction(
  '[Transaction] Cancel Transaction Failure',
  props<{ transactionId: string }>()
);

/** Clear fee-options + any pending fee choice (e.g. leaving the detail page). */
export const clearFeeOptions = createAction('[Transaction] Clear Fee Options');

export const createWithdrawal = createAction(
  '[Transaction] Create Withdrawal',
  props<{ walletId: string; body: CreateWithdrawalRequestBody }>()
);

export const createWithdrawalSuccess = createAction(
  '[Transaction] Create Withdrawal Success',
  props<{ response: CreateWithdrawalResponse | null; status: number }>()
);

export const createWithdrawalFailure = createAction(
  '[Transaction] Create Withdrawal Failure',
  props<{ error: string }>()
);

export const resetState = createAction('[Transaction] Reset State');

/** Create-withdrawal step 2: network fee suggestion from asset-management API */
export const loadCreateFlowNetworkFee = createAction(
  '[Transaction] Load Create Flow Network Fee',
  props<{ walletId: string }>()
);

export const loadCreateFlowNetworkFeeSuccess = createAction(
  '[Transaction] Load Create Flow Network Fee Success',
  props<{ data: NetworkFeeData }>()
);

export const loadCreateFlowNetworkFeeFailure = createAction(
  '[Transaction] Load Create Flow Network Fee Failure',
  props<{ error: string }>()
);

export const clearCreateFlowNetworkFee = createAction(
  '[Transaction] Clear Create Flow Network Fee'
);

/** Create-withdrawal step 2: GET /wallet/:id/withdrawal/limit-summary */
export const loadCreateFlowWithdrawalLimitSummary = createAction(
  '[Transaction] Load Create Flow Withdrawal Limit Summary',
  props<{ walletId: string }>()
);

export const loadCreateFlowWithdrawalLimitSummarySuccess = createAction(
  '[Transaction] Load Create Flow Withdrawal Limit Summary Success',
  props<{ data: WithdrawalLimitSummaryData }>()
);

export const loadCreateFlowWithdrawalLimitSummaryFailure = createAction(
  '[Transaction] Load Create Flow Withdrawal Limit Summary Failure',
  props<{ error: string }>()
);

/**
 * Detail page step 1: the same limit summary, for the validation card's
 * "remaining today" and "% used" (F-072). Keyed by wallet so a summary for
 * another wallet is never shown.
 */
export const loadDetailWithdrawalLimitSummary = createAction(
  '[Transaction] Load Detail Withdrawal Limit Summary',
  props<{ walletId: string }>()
);

export const loadDetailWithdrawalLimitSummarySuccess = createAction(
  '[Transaction] Load Detail Withdrawal Limit Summary Success',
  props<{ walletId: string; data: WithdrawalLimitSummaryData }>()
);

export const loadDetailWithdrawalLimitSummaryFailure = createAction(
  '[Transaction] Load Detail Withdrawal Limit Summary Failure',
  props<{ walletId: string; error: string }>()
);

/** Create-withdrawal: pricing rates for equivalents (GET /pricing/rates) */
export const loadCreateFlowPricingRate = createAction(
  '[Transaction] Load Create Flow Pricing Rate',
  props<{ baseSymbol: string; quoteSymbol?: string }>()
);

export const loadCreateFlowPricingRateSuccess = createAction(
  '[Transaction] Load Create Flow Pricing Rate Success',
  props<{ baseSymbol: string; quoteSymbol: string; rate: number }>()
);

export const loadCreateFlowPricingRateFailure = createAction(
  '[Transaction] Load Create Flow Pricing Rate Failure',
  props<{ baseSymbol: string; quoteSymbol: string; error: string }>()
);

export const clearCreateFlowPricingRates = createAction(
  '[Transaction] Clear Create Flow Pricing Rates'
);

export const setCreateWithdrawalPrefill = createAction(
  '[Transaction] Set Create Withdrawal Prefill',
  props<{
    prefill: {
      vaultId: string;
      walletId: string;
      whitelistAddressId?: string | null;
    };
  }>()
);

export const clearCreateWithdrawalPrefill = createAction(
  '[Transaction] Clear Create Withdrawal Prefill'
);
